import ScheduleSendOutlined from '@mui/icons-material/ScheduleSendOutlined'
import { Alert, Box, Button, Skeleton, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { keys, useScheduled } from '../api/hooks'
import type { OutboxJob } from '../api/types'
import { confirmDialog } from '../lib/dialogs'
import { OrbitMark } from '../lib/Orbit'
import { fmtWhen } from '../lib/schedule'
import { useUi } from '../store/ui'
import { useTokens } from '../theme'

/** Messages queued to send later. Cancel unschedules; Edit unschedules and reopens the draft in a compose window. */
export function ScheduledPage() {
  const t = useTokens()
  const qc = useQueryClient()
  const { openCompose, notify } = useUi()
  const { jobs, isPending, isError, refetch } = useScheduled()
  const refresh = () => qc.invalidateQueries({ queryKey: keys.outbox })

  const cancel = async (j: OutboxJob) => {
    if (!(await confirmDialog({ title: 'Cancel this scheduled message?', body: 'It will not be sent. You can still write it again from scratch.', confirmLabel: 'Cancel send', danger: true }))) return
    try { await api(`/outbox/${j.id}/cancel`, { method: 'POST' }); notify({ message: 'Scheduled message canceled' }) } catch (e) { notify({ message: e instanceof Error ? e.message : 'Could not cancel', severity: 'error' }) }
    void refresh()
  }
  const edit = async (j: OutboxJob) => {
    try {
      await api(`/outbox/${j.id}/cancel`, { method: 'POST' })
      const p = j.payload
      openCompose({ to: p.to, cc: p.cc, bcc: p.bcc, subject: p.subject, html: p.html ?? '', inReplyTo: p.inReplyTo ?? null, references: p.references })
    } catch (e) { notify({ message: e instanceof Error ? e.message : 'Could not edit', severity: 'error' }) }
    void refresh()
  }
  const retry = async (j: OutboxJob) => { await api(`/outbox/${j.id}/retry`, { method: 'POST' }).catch(() => undefined); void refresh() }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
      <Box sx={{ pl: 2.5, pr: 1, height: 64, display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0 }}>
        <Typography variant="h1" sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15 }}>Scheduled</Typography>
        <Typography variant="caption" className="tabular" color="text.secondary">{jobs.length ? `${jobs.length} scheduled` : 'Nothing scheduled'}</Typography>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isPending ? <Box sx={{ p: 1 }}>{Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={64} />)}</Box>
          : isError ? <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" onClick={() => void refetch()}>Retry</Button>}>Couldn't load scheduled messages.</Alert>
          : jobs.length === 0 ? (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', px: 3, py: 10 }}>
              <OrbitMark><ScheduleSendOutlined /></OrbitMark>
              <Typography variant="h6" component="p" sx={{ mt: 2, fontWeight: 700, letterSpacing: '-0.01em', color: 'text.primary' }}>No scheduled messages</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>Use the arrow next to Send when writing to pick a time.</Typography>
            </Box>
          ) : jobs.map((j) => {
            const failed = j.status === 'FAILED'
            return (
              <Box key={j.id} role="row" sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, minHeight: 64, borderBottom: 1, borderColor: 'divider', '&:hover': { bgcolor: t.canvas } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 700 }}>{j.payload.subject || '(no subject)'}</Typography>
                  <Typography noWrap variant="body2" color="text.secondary">To {j.payload.to.concat(j.payload.cc).join(', ') || 'undisclosed'}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="body2" className="tabular" sx={{ fontWeight: 600, color: failed ? 'error.main' : 'text.primary' }}>{failed ? 'Failed to send' : fmtWhen(j.payload.sendAt!)}</Typography>
                  {failed && j.error && <Typography variant="caption" color="text.secondary">{j.error}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {failed ? <Button size="small" onClick={() => void retry(j)}>Retry</Button> : j.payload.attachmentIds.length === 0 && <Button size="small" onClick={() => void edit(j)}>Edit</Button>}
                  <Button size="small" color="inherit" onClick={() => void cancel(j)}>Cancel send</Button>
                </Box>
              </Box>
            )
          })}
      </Box>
    </Box>
  )
}
